import React, { useImperativeHandle, useState } from 'react'
import { assert } from 'tsafe'
import type { CommonDeps } from '../base/CommonDeps'
import { DialogWithIcon } from '../ui/Dialog'
import type { BarcodeResult } from './barcodeTypes'
import type { KnownUnit } from './unit'

enum ModalState {
    'Closed',
    'NoUnitMatched',
    'MatchedUnitNotOnTodoList',
}

export interface UnitQRScannerProps {
    deps: CommonDeps
    onSelect: (id: number) => void
    doScan: () => Promise<BarcodeResult>
    onRedirect?: (path: string) => void
    unitsToFindResultWithin?: KnownUnit[]
}

export interface UnitQRCodeScanningResultModalRef {
    startScan: () => void
}

export function UnitQRScanButton(props: React.PropsWithChildren<UnitQRScannerProps>) {
    const modalRef = React.useRef<UnitQRCodeScanningResultModalRef>()

    return (
        <>
            <zaptic-button
                data-testid="unit-qr-scan-button"
                onClick={() => modalRef.current.startScan()}
                type="tertiary"
                size="large"
                no-text-mobile={true}
            >
                {props.deps.i18n('app:Todo.page.scan')}
                <zaptic-icon slot="icon" icon-name="barcode-2" />
            </zaptic-button>
            <UnitQRCodeScanningResultModal {...props} ref={modalRef} />
        </>
    )
}

export const UnitQRCodeScanningResultModal = React.forwardRef(
    (props: React.PropsWithChildren<UnitQRScannerProps>, ref: React.Ref<UnitQRCodeScanningResultModalRef>) => {
        const [modalState, setModalState] = useState<ModalState>(ModalState.Closed)
        const [matchedUnitName, setMatchedUnitName] = useState('')

        useImperativeHandle(ref, () => ({ startScan }))

        const primaryButton = {
            label: props.deps.i18n('app:Todo.page.qrCodeScanModal.tryAgainButtonLabel'),
            onClick: () => startScan(),
        }
        const secondaryButton = {
            label: props.deps.i18n('app:Todo.page.qrCodeScanModal.closeButtonLabel'),
            onClick: () => close(),
        }

        return (
            {
                [ModalState.NoUnitMatched]: (
                    <DialogWithIcon
                        iconName="search-no-result"
                        title={props.deps.i18n('app:Todo.page.qrCodeScanModal.noMatches')}
                        description={props.deps.i18n('app:Todo.page.qrCodeScanModal.noMatchesDescription')}
                        primaryButton={primaryButton}
                        secondaryButton={secondaryButton}
                    />
                ),
                [ModalState.MatchedUnitNotOnTodoList]: (
                    <DialogWithIcon
                        iconName="empty-box"
                        title={props.deps.i18n('app:Todo.page.qrCodeScanModal.nothingToShow')}
                        description={props.deps.i18n('app:Todo.page.qrCodeScanModal.nothingToShowDescription', {
                            value: matchedUnitName,
                        })}
                        primaryButton={primaryButton}
                        secondaryButton={secondaryButton}
                    />
                ),
                [ModalState.Closed]: null,
            }[modalState] || null
        )

        function close() {
            setModalState(ModalState.Closed)
        }

        async function startScan() {
            const barcodeResult = await props.doScan()

            setMatchedUnitName('')
            ;({
                Cancelled: () => {
                    close()
                },
                NotFound: () => {
                    setModalState(ModalState.NoUnitMatched)
                },
                Success: () => {
                    assert(barcodeResult.type === 'Success')
                    if (
                        props.unitsToFindResultWithin &&
                        props.unitsToFindResultWithin.find((unit) => unit.id === barcodeResult.unit.id)
                    ) {
                        props.onSelect(barcodeResult.unit.id)
                        setModalState(ModalState.Closed)
                    } else {
                        setMatchedUnitName(props.deps.I18N_replaceNameSlug(barcodeResult.unit).name)
                        setModalState(ModalState.MatchedUnitNotOnTodoList)
                    }
                },
                Redirect: () => {
                    assert(barcodeResult.type === 'Redirect')
                    // If the onRedirect prop is passed handle the redirection otherwise
                    // treat it as a not found.
                    if (props.onRedirect) {
                        props.onRedirect(barcodeResult.path)
                    } else {
                        setModalState(ModalState.NoUnitMatched)
                    }
                },
            })[barcodeResult.type]()
        }

        /** for desktop testing mocking qr scan */
        // function startScan() {
        //     console.log({ modalState })
        //     const unitsToFindResultWithin = props.unitsToFindResultWithin || [{ name: 'name', id: 31 }]
        //     const randomIndex = Math.floor(Math.random() * unitsToFindResultWithin.length)
        //     if (modalState === ModalState.Closed) {
        //         setModalState(ModalState.NoUnitMatched)
        //     } else if (modalState === ModalState.NoUnitMatched) {
        //         setModalState(ModalState.MatchedUnitNotOnTodoList)
        //         setMatchedUnitName(props.deps.I18N_replaceNameSlug(unitsToFindResultWithin[randomIndex])?.name)
        //     } else {
        //         setModalState(ModalState.Closed)
        //         const randomIndex = Math.floor(Math.random() * unitsToFindResultWithin.length)
        //         props.onSelect(unitsToFindResultWithin[randomIndex]?.id)
        //     }
        //     console.log({ modalState })
        // }
    }
)
